import { useState, useRef, useEffect } from "react";
import { BsClock } from "react-icons/bs";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";

const convertMinutesToTime = (minutes: number): string => {
  if (minutes === 1440) return "11:59 PM";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${mins.toString().padStart(2, "0")} ${period}`;
};

const formatShifts = (shifts: any[]): string => {
  if (!shifts || shifts.length === 0) return "Closed";
  return shifts
    .map(
      (shift) =>
        `${convertMinutesToTime(shift.start)} - ${convertMinutesToTime(
          shift.end
        )}`
    )
    .join(", ");
};

const getDaySchedule = (workingHours: any, day: string): string => {
  const dayData = workingHours[day.toLowerCase()];
  if (!dayData) return "Closed";
  return formatShifts(dayData.shifts || []);
};

const getTodaySchedule = (
  workingHours: any
): { day: string; schedule: string } => {
  const days = ["su", "mo", "tu", "we", "th", "fr", "sa"];
  const dayNames = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  const today = new Date().getDay();
  return {
    day: dayNames[today],
    schedule: getDaySchedule(workingHours, days[today]),
  };
};

interface BusinessHoursDisplayProps {
  workingHours: any;
}

const BusinessHoursDisplay = ({ workingHours }: BusinessHoursDisplayProps) => {
  const [isHoursOpen, setIsHoursOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsHoursOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!workingHours) return null;

  const todaySchedule = getTodaySchedule(workingHours);

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className="flex items-center space-x-3 cursor-pointer bg-gray-50 hover:bg-gray-200 p-2 rounded-lg transition-colors w-1/3"
        onClick={() => setIsHoursOpen(!isHoursOpen)}
      >
        <BsClock className="text-gray-400" />
        <div className="flex-grow">
          <span className="font-medium">{todaySchedule.day}:</span>
          <span className="ml-2">{todaySchedule.schedule}</span>
        </div>
        {isHoursOpen ? (
          <FiChevronUp className="text-gray-400" />
        ) : (
          <FiChevronDown className="text-gray-400" />
        )}
      </div>

      {isHoursOpen && (
        <div className="absolute z-10 mt-2 w-2/3 bg-white rounded-lg shadow-lg border border-gray-200 p-4">
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div className="text-gray-600">Monday:</div>
              <div>{getDaySchedule(workingHours, "mo")}</div>
              <div className="text-gray-600">Tuesday:</div>
              <div>{getDaySchedule(workingHours, "tu")}</div>
              <div className="text-gray-600">Wednesday:</div>
              <div>{getDaySchedule(workingHours, "we")}</div>
              <div className="text-gray-600">Thursday:</div>
              <div>{getDaySchedule(workingHours, "th")}</div>
              <div className="text-gray-600">Friday:</div>
              <div>{getDaySchedule(workingHours, "fr")}</div>
              <div className="text-gray-600">Saturday:</div>
              <div>{getDaySchedule(workingHours, "sa")}</div>
              <div className="text-gray-600">Sunday:</div>
              <div>{getDaySchedule(workingHours, "su")}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessHoursDisplay;
